import { forwardRef } from 'react';
import { ChevronUp } from 'lucide-react';
import { ProgressRing } from './ProgressRing';
import { COPY, ONBOARDING_STEPS } from '../config/onboarding.config';
import type { StepKey } from '../config/onboarding.types';

interface ChecklistLauncherProps {
  completed: number;
  /** Primer paso pendiente; null cuando ya están los tres. */
  nextStep: StepKey | null;
  isReady: boolean;
  isOpen: boolean;
  /** Se completó un paso con el popover cerrado. */
  hasAlert: boolean;
  /** id del popover que controla, para aria-controls. */
  controlsId: string;
  onToggle: () => void;
}

/**
 * Estado colapsado: píldora fija abajo a la derecha. Reemplaza a ChecklistTab.
 * Dice qué hacer ("Agrega tu producto"), no cuánto falta: eso ya lo muestra
 * el anillo. Mide ~170px y queda fuera de la zona de lectura.
 */
export const ChecklistLauncher = forwardRef<HTMLButtonElement, ChecklistLauncherProps>(
  function ChecklistLauncher(
    { completed, nextStep, isReady, isOpen, hasAlert, controlsId, onToggle },
    ref,
  ) {
    const next = ONBOARDING_STEPS.find((s) => s.key === nextStep);
    const eyebrow = isReady ? COPY.launcher.readyEyebrow : COPY.launcher.nextEyebrow;
    const label = isReady || !next ? COPY.launcher.readyLabel : next.nextLabel;

    return (
      <button
        ref={ref}
        type="button"
        onClick={onToggle}
        aria-expanded={isOpen}
        aria-controls={controlsId}
        aria-label={`${COPY.title(true)}: ${label}`}
        className={[
          'pointer-events-auto relative flex items-center gap-2.5 rounded-full border bg-surface-0 py-1.5 pl-1.5 pr-3.5',
          'shadow-[0_10px_28px_-10px_rgba(15,23,42,0.35),0_2px_6px_rgba(15,23,42,0.06)]',
          'transition-[border-color,box-shadow] duration-200',
          'hover:shadow-[0_14px_32px_-10px_rgba(15,23,42,0.40),0_2px_6px_rgba(15,23,42,0.08)]',
          'motion-safe:animate-launchIn',
          isOpen ? 'border-brand' : 'border-muted hover:border-[var(--border-default)]',
        ].join(' ')}
      >
        <ProgressRing value={completed} size={32} />

        <span className="flex flex-col text-left leading-tight">
          <span className={['text-label font-medium', isReady ? 'text-brand' : 'text-muted'].join(' ')}>
            {eyebrow}
          </span>
          <span className={['whitespace-nowrap text-ui-sm font-semibold', isReady ? 'text-brand' : 'text-primary'].join(' ')}>
            {label}
          </span>
        </span>

        <ChevronUp
          className={[
            'h-[15px] w-[15px] shrink-0 transition-transform duration-300',
            isOpen ? 'rotate-180 text-brand' : 'text-subtle',
          ].join(' ')}
          strokeWidth={2.4}
        />

        {/* Aviso de avance: punto fijo con un halo que pulsa 3 veces y se detiene. */}
        {hasAlert && (
          <span
            aria-hidden
            className="absolute left-[30px] top-[3px] h-2.5 w-2.5 rounded-full border-2 border-[var(--surface-0)] bg-brand"
          >
            <span className="absolute -inset-0.5 rounded-full border-2 border-brand opacity-0 motion-safe:animate-halo" />
          </span>
        )}
      </button>
    );
  },
);
